import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    LOCALE_ID,
    PLATFORM_ID,
    type ResourceRef,
    Signal,
    WritableSignal,
    afterNextRender,
    computed,
    effect,
    inject,
    resource,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import {
    EBlockType,
    IBlock,
    type ISiteContentsEntry,
    itemLinkOfContent,
    listOfContent,
    siteHtmlOf,
    titledOfContent,
} from '@rt-tools/cms-contract';
import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import { RtSkeletonWrapperComponent } from '@rt-tools/ui-kit-v2/skeleton-wrapper';
import { BlogArticlesApiService, IBlogArticle, type IStoredBlogArticle, blogCardsOf } from '@stair-craft/blog/data-access';
import {
    BLOG_BLOCK_PAGE,
    BlogAccordionBlockComponent,
    BlogArticleCardComponent,
    BlogButtonBlockComponent,
    BlogEmbedBlockComponent,
    BlogImageBlockComponent,
    BlogProsConsBlockComponent,
    BlogQuoteBlockComponent,
    type IBlogBlockPage,
} from '@stair-craft/blog/ui';
import { PageHeaderComponent } from '@stair-craft/common/components';
import { PageHeaderService, SITE_ORIGIN } from '@stair-craft/common/util';

const TOC_CLICK_SUPPRESS_MS: number = 800;
const RELATED_COUNT: number = 3;
const JSON_LD_MARKER_ATTR: string = 'data-sc-blog-schema';
const BEM_BLOCK: string = 'sc-blog-article-page';
const HEADING_ID_PREFIX: string = 'h-';
const PREVIEW_TOKEN_PARAM: string = 'previewToken';

// Блок тела с тем, что шаблону нужно для вывода: номер заголовка H2, пункты списка, заметка и
// статья, на которую ведёт блок ссылки на страницу.
interface IArticleBlockView {
    readonly block: IBlock.Base;
    /** HTML текстового блока с выделением тегами — то, что выводится на страницу. */
    readonly html: string;
    readonly headingId: string;
    readonly number: string;
    readonly items: readonly string[];
    readonly note: IBlock.Content.Note | null;
    readonly linked: IBlogArticle.State | null;
}

function headingIdOf(blockId: string): string {
    return `${HEADING_ID_PREFIX}${blockId}`;
}

function numberOf(position: number): string {
    return position < 10 ? `0${String(position)}` : String(position);
}

// Сайты по языку статьи: ссылки `hreflang` страницы статьи хранилища называют только сайты, где
// статья с этим адресом опубликована на их языке.
const HREFLANGS_OF_LOCALE: Readonly<Record<string, readonly string[]>> = {
    en: ['en', 'x-default'],
    ru: ['ru-RU', 'ru-BY'],
};

@Component({
    selector: 'sc-blog-article-page',
    templateUrl: './blog-article-page.component.html',
    styleUrl: './blog-article-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        RtSkeletonWrapperComponent,

        // components
        BlogAccordionBlockComponent,
        BlogArticleCardComponent,
        BlogButtonBlockComponent,
        BlogEmbedBlockComponent,
        BlogImageBlockComponent,
        BlogProsConsBlockComponent,
        BlogQuoteBlockComponent,
        PageHeaderComponent,
    ],
    providers: [{ provide: BLOG_BLOCK_PAGE, useFactory: (): Signal<IBlogBlockPage> => inject(BlogArticlePageComponent).blockPage }],
    host: {
        class: BEM_BLOCK,
    },
})
export default class BlogArticlePageComponent {
    readonly #route: ActivatedRoute = inject(ActivatedRoute);
    readonly #router: Router = inject(Router);
    readonly #title: Title = inject(Title);
    readonly #meta: Meta = inject(Meta);
    readonly #doc: Document = inject(DOCUMENT);
    readonly #siteOrigin: string = inject(SITE_ORIGIN);
    readonly #localeId: string = inject(LOCALE_ID);
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #pageHeader: PageHeaderService = inject(PageHeaderService);
    readonly #params: Signal<ParamMap | undefined> = toSignal(this.#route.paramMap);
    readonly #query: Signal<ParamMap | undefined> = toSignal(this.#route.queryParamMap);
    readonly #api: BlogArticlesApiService = inject(BlogArticlesApiService);

    // Черновик открывается по токену предпросмотра из строки запроса.
    readonly #previewToken: Signal<string> = computed((): string => this.#query()?.get(PREVIEW_TOKEN_PARAM) ?? '');

    readonly #stored: ResourceRef<IStoredBlogArticle | null | undefined> = resource({
        params: (): { slug: string | null; previewToken: string } => ({ slug: this.slug(), previewToken: this.#previewToken() }),
        loader: ({ params }: { params: { slug: string | null; previewToken: string } }): Promise<IStoredBlogArticle | null> =>
            params.slug === null ? Promise.resolve(null) : this.#api.article(params.slug, this.#localeId, params.previewToken),
    });

    readonly #storedList: ResourceRef<readonly IBlogArticle.State[] | undefined> = resource({
        loader: (): Promise<readonly IBlogArticle.State[]> => this.#api.published(this.#localeId),
    });

    readonly #tags: ResourceRef<readonly IBlogArticle.Tag[] | undefined> = resource({
        loader: (): Promise<readonly IBlogArticle.Tag[]> => this.#api.tags(),
    });

    readonly #published: Signal<readonly IBlogArticle.State[]> = computed(() => this.#storedList.value() ?? []);

    // Ссылки `hreflang`, снятые со страницы статьи хранилища; возвращаются при уходе со страницы.
    #hiddenAlternates: HTMLLinkElement[] = [];

    #scrollHandler: (() => void) | null = null;
    #rafHandle: number | null = null;
    #suppressUntil: number = 0;

    protected readonly BlockType: typeof EBlockType = EBlockType;

    protected readonly slug: Signal<string | null> = computed((): string | null => this.#params()?.get('slug') ?? null);
    protected readonly article: Signal<IBlogArticle.State | null> = computed(
        (): IBlogArticle.State | null => this.#stored.value()?.article ?? null
    );
    // On navigation inside the site the article arrives after the page: placeholders hold its place.
    protected readonly loading: Signal<boolean> = computed(() => this.#stored.isLoading());
    protected readonly activeSectionId: WritableSignal<string> = signal<string>('');

    // Оглавление — заголовки H2 тела; ссылка ведёт на номер заголовка на странице.
    protected readonly contents: Signal<readonly ISiteContentsEntry[]> = computed(() =>
        (this.article()?.contents ?? []).map((entry: ISiteContentsEntry) => ({ ...entry, blockId: headingIdOf(entry.blockId) }))
    );

    protected readonly blocks: Signal<readonly IArticleBlockView[]> = computed((): readonly IArticleBlockView[] => {
        const byId: ReadonlyMap<string, IBlogArticle.State> = new Map(
            this.#published().map((article: IBlogArticle.State) => [article.id, article])
        );
        let headings: number = 0;
        return (this.article()?.blocks ?? []).map((block: IBlock.Base): IArticleBlockView => {
            headings += block.type === EBlockType.Heading2 ? 1 : 0;
            return {
                block,
                html: siteHtmlOf(block.content),
                headingId: headingIdOf(block.id),
                number: block.type === EBlockType.Heading2 ? numberOf(headings) : '',
                items: listOfContent(block.content).map(siteHtmlOf),
                note: block.type === EBlockType.Note ? this.#noteOf(block.content) : null,
                linked:
                    block.type === EBlockType.ContentItemLink ? (byId.get(itemLinkOfContent(block.content).contentItemId) ?? null) : null,
            };
        });
    });

    protected readonly relatedArticles: Signal<readonly IBlogArticle.Card[]> = computed((): readonly IBlogArticle.Card[] => {
        const current: IBlogArticle.State | null = this.article();
        if (current === null) {
            return [];
        }
        const all: readonly IBlogArticle.State[] = this.#published();
        const idx: number = all.findIndex((a: IBlogArticle.State): boolean => a.path === current.path);
        if (idx === -1) {
            return [];
        }
        const related: IBlogArticle.State[] = [];
        for (let offset: number = 1; offset <= Math.min(RELATED_COUNT, all.length - 1); offset++) {
            related.push(all[(idx + offset) % all.length]);
        }
        return blogCardsOf(related, this.#tags.value() ?? []);
    });

    // Данные страницы для блоков тела: заголовок статьи и пути опубликованных страниц по номеру
    // единицы — для подписей и для кнопок, ведущих на страницу сайта.
    public readonly blockPage: Signal<IBlogBlockPage> = computed((): IBlogBlockPage => ({
        title: this.article()?.title ?? '',
        paths: new Map(this.#published().map((article: IBlogArticle.State) => [article.id, article.path])),
    }));

    constructor() {
        effect((): void => {
            const article: IBlogArticle.State | null = this.article();
            if (article === null) {
                if (this.slug() !== null && !this.#stored.isLoading()) {
                    void this.#router.navigate(['/blog']);
                }
                return;
            }
            this.#applySeo(article);
            this.#applyAlternates(this.#stored.value()?.locales ?? []);
            this.#applyRobots(this.#previewToken() !== '');
            this.#pageHeader.setLeaf(article.title);
            this.activeSectionId.set(this.contents()[0]?.blockId ?? '');
        });

        afterNextRender((): void => {
            if (!isPlatformBrowser(this.#platformId)) {
                return;
            }
            this.#initScrollSpy();
        });

        this.#destroyRef.onDestroy((): void => {
            if (this.#scrollHandler !== null) {
                globalThis.removeEventListener('scroll', this.#scrollHandler);
                globalThis.removeEventListener('resize', this.#scrollHandler);
            }
            if (this.#rafHandle !== null) {
                globalThis.cancelAnimationFrame(this.#rafHandle);
            }
            this.#pageHeader.setLeaf(null);
            this.#removeManagedJsonLd();
            this.#applyAlternates(null);
            this.#applyRobots(false);
        });
    }

    protected onTocClick(event: MouseEvent, sectionId: string): void {
        event.preventDefault();
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        const target: HTMLElement | null = this.#doc.getElementById(sectionId);
        if (target === null) {
            return;
        }
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.activeSectionId.set(sectionId);
        this.#suppressUntil = Date.now() + TOC_CLICK_SUPPRESS_MS;
        globalThis.history.replaceState(null, '', `#${sectionId}`);
    }

    #noteOf(content: string): IBlock.Content.Note {
        const note: IBlock.Content.Note = titledOfContent(content);
        return { ...note, text: siteHtmlOf(note.text) };
    }

    // Страница предпросмотра закрыта от индексации; уход со страницы снимает запрет.
    #applyRobots(preview: boolean): void {
        if (preview) {
            this.#meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
            return;
        }
        this.#meta.removeTag('name="robots"');
    }

    #applySeo(article: IBlogArticle.State): void {
        const articleUrl: string = `${this.#siteOrigin}${article.path}`;
        const imageUrl: string | null = article.imageSrc;

        this.#title.setTitle(article.metaTitle);
        this.#meta.updateTag({ name: 'description', content: article.metaDescription });
        this.#meta.updateTag({ property: 'og:type', content: 'article' });
        this.#meta.updateTag({ property: 'og:title', content: article.metaTitle });
        this.#meta.updateTag({ property: 'og:description', content: article.metaDescription });
        this.#meta.updateTag({ property: 'og:url', content: articleUrl });
        this.#meta.updateTag({ name: 'twitter:title', content: article.metaTitle });
        this.#meta.updateTag({ name: 'twitter:description', content: article.metaDescription });

        if (imageUrl !== null) {
            this.#meta.updateTag({ property: 'og:image', content: imageUrl });
            this.#meta.updateTag({ property: 'og:image:alt', content: article.title });
            this.#meta.updateTag({ property: 'og:image:type', content: 'image/webp' });
            // Article images are not 1200×630 brand banners; remove the static dims
            // from index.html so platforms infer them from the file itself.
            this.#meta.removeTag('property="og:image:width"');
            this.#meta.removeTag('property="og:image:height"');
            this.#meta.updateTag({ name: 'twitter:image', content: imageUrl });
            this.#meta.updateTag({ name: 'twitter:image:alt', content: article.title });
        }

        this.#meta.updateTag({ property: 'article:published_time', content: article.publishedAt });
        this.#meta.updateTag({ property: 'article:modified_time', content: article.publishedAt });

        this.#setManagedJsonLd(this.#buildJsonLd(article, articleUrl, imageUrl));
    }

    // `null` — уход со страницы: все ссылки возвращаются на место.
    #applyAlternates(locales: readonly string[] | null): void {
        const head: HTMLHeadElement = this.#doc.head;
        this.#hiddenAlternates.forEach((link: HTMLLinkElement) => head.appendChild(link));
        this.#hiddenAlternates = [];
        if (locales === null) {
            return;
        }
        const allowed: ReadonlySet<string> = new Set<string>(locales.flatMap((locale: string) => HREFLANGS_OF_LOCALE[locale] ?? []));
        this.#doc.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]').forEach((link: HTMLLinkElement) => {
            if (!allowed.has(link.getAttribute('hreflang') ?? '')) {
                link.remove();
                this.#hiddenAlternates.push(link);
            }
        });
    }

    #buildJsonLd(article: IBlogArticle.State, articleUrl: string, imageUrl: string | null): string {
        const homeName: string = $localize`:@@navHome:Главная`;
        const blogName: string = $localize`:@@navBlog:Блог`;
        const schema: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'BlogPosting',
                    headline: article.metaTitle,
                    description: article.metaDescription,
                    datePublished: article.publishedAt,
                    dateModified: article.publishedAt,
                    inLanguage: this.#localeId,
                    url: articleUrl,
                    mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
                    isPartOf: { '@type': 'WebSite', url: `${this.#siteOrigin}/` },
                    ...(imageUrl !== null ? { image: imageUrl } : {}),
                },
                {
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        { '@type': 'ListItem', position: 1, name: homeName, item: `${this.#siteOrigin}/` },
                        { '@type': 'ListItem', position: 2, name: blogName, item: `${this.#siteOrigin}/blog` },
                        { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
                    ],
                },
            ],
        };
        return JSON.stringify(schema);
    }

    #setManagedJsonLd(jsonText: string): void {
        let script: HTMLScriptElement | null = this.#doc.querySelector(`script[${JSON_LD_MARKER_ATTR}]`);
        if (!script) {
            script = this.#doc.createElement('script');
            script.setAttribute('type', 'application/ld+json');
            script.setAttribute(JSON_LD_MARKER_ATTR, '');
            this.#doc.head.appendChild(script);
        }
        script.textContent = jsonText;
    }

    #removeManagedJsonLd(): void {
        const script: HTMLScriptElement | null = this.#doc.querySelector(`script[${JSON_LD_MARKER_ATTR}]`);
        script?.remove();
    }

    #initScrollSpy(): void {
        const updateActive: () => void = (): void => {
            this.#rafHandle = null;
            if (Date.now() < this.#suppressUntil) {
                return;
            }
            const article: IBlogArticle.State | null = this.article();
            if (article === null) {
                return;
            }
            const sections: HTMLElement[] = this.contents()
                .map((entry: ISiteContentsEntry): HTMLElement | null => this.#doc.getElementById(entry.blockId))
                .filter((el: HTMLElement | null): el is HTMLElement => el !== null);
            if (sections.length === 0) {
                return;
            }
            this.activeSectionId.set(this.#computeActiveId(sections));
        };

        this.#scrollHandler = (): void => {
            if (this.#rafHandle !== null) {
                return;
            }
            this.#rafHandle = globalThis.requestAnimationFrame(updateActive);
        };

        updateActive();
        globalThis.addEventListener('scroll', this.#scrollHandler, { passive: true });
        globalThis.addEventListener('resize', this.#scrollHandler, { passive: true });
    }

    #computeActiveId(sections: readonly HTMLElement[]): string {
        const probeY: number = globalThis.innerHeight / 2;
        let bestId: string = sections[0].id;
        let bestTop: number = -Infinity;

        for (const section of sections) {
            const top: number = section.getBoundingClientRect().top;
            if (top <= probeY && top > bestTop) {
                bestTop = top;
                bestId = section.id;
            }
        }
        return bestId;
    }
}
